// src/pages/BookingPage.js

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import VideoStep from '../components/VideoStep';
import PhotoGallery from '../components/PhotoGallery';
import ServiceList from '../components/ServiceList';
import CalendarSelector from '../components/CalendarSelector';
import HourPicker from '../components/HourPicker';
import DisclaimerStep from '../components/DisclaimerStep';
import ClientIdentification from '../components/ClientIdentification';
import WhatsAppLink from '../components/WhatsAppLink';

export default function ModelFunnel() {
  const { username } = useParams();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [funnelData, setFunnelData] = useState({
    video: null,
    photos: null,
    service: null,
    date: null,
    time: null,
    disclaimer: null,
    clientInfo: null,
    whatsapp: null
  });

  useEffect(() => {
    const fetchModelData = async () => {
      try {
        const modelsRef = collection(db, 'models');
        const q = query(modelsRef, where('username', '==', username));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          setError('Modelo não encontrado');
          return;
        }

        const modelData = querySnapshot.docs[0].data();
        console.log('Model Data:', modelData);
        console.log('Availability Slots:', modelData.availabilitySlots);
        setModel(modelData);
      } catch (err) {
        console.error('Error fetching model:', err);
        setError('Erro ao carregar dados do modelo');
      } finally {
        setLoading(false);
      }
    };

    fetchModelData();
  }, [username]);

  const handleVideoComplete = (videoData) => {
    setFunnelData(prev => ({ ...prev, video: videoData }));
    setStep(2);
  };

  const handlePhotoGalleryComplete = (photosData) => {
    setFunnelData(prev => ({ ...prev, photos: photosData }));
    setStep(3);
  };

  const handleServiceSelect = (service) => {
    setFunnelData(prev => ({ ...prev, service }));
    setStep(4);
  };

  const handleDateSelect = (date) => {
    setFunnelData(prev => ({ ...prev, date }));
    setStep(5);
  };

  const handleTimeSelect = (time) => {
    setFunnelData(prev => ({ ...prev, time }));
    setStep(6);
  };

  const handleDisclaimerAccept = (disclaimerData) => {
    setFunnelData(prev => ({ ...prev, disclaimer: disclaimerData }));
    setStep(7);
  };

  const handleClientInfoSubmit = (clientInfo) => {
    setFunnelData(prev => ({ ...prev, clientInfo }));
    setStep(8);
  };

  const handleWhatsAppComplete = (whatsappData) => {
    setFunnelData(prev => ({ ...prev, whatsapp: whatsappData }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#e91e63] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erro</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Modelo não encontrado</h2>
          <p className="text-gray-600">O modelo solicitado não foi encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 px-2 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6 sm:mb-10"
        >
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 leading-tight">{model.name}</h1>
          <p className="text-gray-600 text-base sm:text-xl leading-relaxed">{model.type}</p>
        </motion.div>

        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg p-4 sm:p-8 lg:p-10">
          {/* Progress Bar */}
          <div className="mb-8 sm:mb-12">
            <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                className="absolute top-0 left-0 h-full bg-[#e91e63]"
                initial={{ width: "0%" }}
                animate={{ width: `${((step - 1) / 7) * 100}%` }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs sm:text-sm">
              {[
                { key: 1, name: 'Vídeo' },
                { key: 2, name: 'Fotos' },
                { key: 3, name: 'Serviços' },
                { key: 4, name: 'Data' },
                { key: 5, name: 'Horário' },
                { key: 6, name: 'Termos' },
                { key: 7, name: 'Dados' },
                { key: 8, name: 'Final' }
              ].map(({ key, name }) => (
                <div
                  key={key}
                  className={`text-center ${key === step ? 'text-[#e91e63] font-medium' : 'text-gray-500'}`}
                >
                  <span className="hidden sm:inline">{name}</span>
                  <span className="sm:hidden">{key}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="min-h-[300px] sm:min-h-[400px]"
          >
            {step === 1 && (
              <VideoStep
                videoUrl={model.videoUrl}
                audioUrl={model.audioUrl}
                onNext={handleVideoComplete}
                onError={setError}
              />
            )}

            {step === 2 && (
              <PhotoGallery
                photos={model.pictures || []}
                onComplete={handlePhotoGalleryComplete}
                onBack={() => setStep(1)}
              />
            )}

            {step === 3 && (
              <ServiceList
                services={model.services}
                onComplete={() => setStep(4)}
                onBack={() => setStep(2)}
              />
            )}

            {step === 4 && (
              <CalendarSelector
                availabilitySlots={model.availabilitySlots}
                onSelect={handleDateSelect}
              />
            )}

            {step === 5 && (
              <HourPicker
                availabilitySlots={model.availabilitySlots}
                selectedDate={funnelData.date}
                onSelect={handleTimeSelect}
                onBack={() => setStep(4)}
              />
            )}

            {step === 6 && (
              <DisclaimerStep
                onNext={handleDisclaimerAccept}
                onBack={() => setStep(5)}
              />
            )}

            {step === 7 && (
              <ClientIdentification
                initial={{}}
                onNext={handleClientInfoSubmit}
                onError={setError}
              />
            )}

            {step === 8 && (
              <WhatsAppLink
                model={model}
                funnelData={funnelData}
                onComplete={handleWhatsAppComplete}
              />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
